/**
 * The Mealz skin for the vendor field controls, in one place.
 *
 * Nuxt UI's default theme (`bg-default`, `ring-accented`, size `md`'s
 * `text-base/5`) does not match the field skin this application has always
 * used -- the canonical control in `app/components/forms/inputs/*.vue`
 * (PhoneInput, PasswordInput, TimezoneSelect). Every shared field molecule
 * binds these slots instead, so the skin changes once and reaches all of them.
 *
 * The molecules drive the vendor controls with `variant="none"`: that leaves
 * `text-highlighted bg-transparent focus:outline-none` as the only vendor
 * paint, so these classes are the whole skin and no vendor variant or
 * compound-variant class has to be neutralized. `focus:outline-solid` removes
 * that variant's `focus:outline-none`, which would otherwise cancel the
 * outline width set below.
 */
export const fieldControlUi = {
  /** `UFormField` slots: label colour, hint and message spacing. */
  formField: {
    container: 'mt-2',
    label: 'block dark:text-content-level-100 font-medium text-content-level-900 text-sm/6',
    error: 'mt-1.5 text-danger text-sm',
    description: 'sr-only',
    help: 'mt-1.5 text-content-level-400 text-sm',
    hint: 'contents text-content-level-400 text-sm',
    labelWrapper: 'justify-start'
  },
  /** `UInput` / `UTextarea` slots. Pair `base` with `outline` or `outlineInvalid`. */
  control: {
    root: 'w-full',
    base: '-outline-offset-1 bg-surface-canvas block dark:bg-surface-canvas/5 dark:placeholder:text-content-level-500 dark:text-content-inverse focus:-outline-offset-2 focus:outline-2 focus:outline-solid focus-visible:outline-2 outline-1 placeholder:text-content-level-400 px-3 py-1.5 ring-0 rounded-md sm:text-sm/6 text-base text-content-level-900 w-full',
    outline:
      'outline-focus-level-300 focus:outline-primary dark:outline-focus-inverse/10 dark:focus:outline-primary',
    outlineInvalid: 'outline-danger-500 focus:outline-danger-500 dark:outline-danger-500',
    focusWithin:
      'focus-within:-outline-offset-2 focus-within:outline-2 focus-within:outline-primary dark:focus-within:outline-primary'
  },
  /** `UPinInput` slots, matching the OTP control in `PhoneOtpInput.vue`. */
  pinInput: {
    root: 'gap-2 sm:gap-3',
    base: 'size-auto h-12 w-10 -outline-offset-1 bg-surface-canvas dark:bg-surface-canvas/5 dark:text-content-inverse focus:-outline-offset-2 focus:outline-2 focus:outline-solid focus-visible:outline-2 font-semibold outline-1 ring-0 rounded-md sm:h-14 sm:w-12 sm:text-xl text-center text-content-level-900 text-lg',
    outline:
      'outline-focus-level-300 focus:outline-primary dark:outline-focus-inverse/10 dark:focus:outline-primary',
    outlineInvalid: 'outline-danger-500 focus:outline-danger-500 dark:outline-danger-500'
  },
  /** `UButton` slots for the password visibility toggle, per the kit's gray field icon. */
  passwordToggle: {
    leadingIcon: 'text-content-level-500 dark:text-content-level-400'
  },
  /** `USelect` slots, reached through `VendorSelect.base.vue`. */
  select: {
    base: '-outline-offset-1 bg-surface-canvas cursor-default dark:bg-surface-canvas/5 dark:text-content-inverse focus:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-2 md:text-sm/6 outline-1 pl-3 pr-2 py-1.5 ring-0 rounded-md text-content-level-900 text-sm/6',
    outline:
      'outline-focus-level-300 focus-visible:outline-primary dark:outline-focus-inverse/10 dark:focus-visible:outline-primary',
    outlineInvalid: 'outline-danger-500 focus-visible:outline-danger-500 dark:outline-danger-500',
    placeholder: 'text-content-level-400 dark:text-content-level-500',
    trailingIcon: 'text-content-level-500 dark:text-content-level-400'
  }
} as const
